/*
 * pfc-upload-normalise.js — accept HEIC and PDF uploads by turning them into a
 * JPEG in the browser, before anything else sees the file.
 *
 * Dual export (browser global `PFCUploadNormalise` / node `require()`), like
 * pfc-mount-pattern-core.js. CANONICAL copy here; VENDORED byte-identical into
 * the React studio (shopify-print-studio-TPE/lib/pfc-upload-normalise.js + .d.ts).
 * Change one → re-copy the other and rebuild studio.js.
 *
 * Why convert, not upload as-is: the print master is cut server-side with sharp
 * (dropshipping-project src/lib/services/photo-crop.ts), and prebuilt sharp
 * decodes neither HEIC (HEVC) nor PDF — an original in either format would 422 on
 * the print-files page. The converted JPEG IS the original from then on: the crop
 * box is measured on it, it is what pfc-photo-source.js uploads, and it carries
 * no EXIF (orientation is already applied), so nothing downstream changes.
 *
 *   HEIC/HEIF: the browser's own decoder first (Safari), else heic2any (libheif,
 *              ~1.3MB, fetched only when a HEIC is actually picked).
 *   PDF:       page 1 rendered with pdf.js (fetched on demand) at up to
 *              PDF_MAX_EDGE px on the long edge, on white.
 *
 * iOS is deliberately NOT offered HEIC in `accept`: leaving it out makes the iOS
 * picker hand over a full-resolution JPEG itself, which beats any canvas
 * conversion — iOS canvases cap at 16.7MP and current iPhones shoot 24MP.
 */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (typeof root !== 'undefined') root.PFCUploadNormalise = api;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var HEIC2ANY_SRC = 'https://cdnjs.cloudflare.com/ajax/libs/heic2any/0.0.4/heic2any.min.js';
  var PDFJS_SRC = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
  var PDFJS_WORKER_SRC = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

  var PDF_MAX_EDGE = 7000;
  var PHOTO_QUALITY = 0.92;
  var PDF_QUALITY = 0.95;

  var BASE_TYPES = 'image/jpeg,image/png';
  var HEIC_TYPES = 'image/heic,image/heif,.heic,.heif';
  var PDF_TYPES = 'application/pdf,.pdf';

  function isIOS() {
    if (typeof navigator === 'undefined') return false;
    var ua = navigator.userAgent || '';
    return /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  }
  // Largest canvas we trust: iOS hard-fails above 16.7MP (blank canvas, no error).
  function maxArea() { return isIOS() ? 16000000 : 40000000; }

  /* The file input `accept` value. `extra` adds types a caller also takes
   * (e.g. 'image/webp' for the studio). */
  function accept(extra) {
    var parts = [BASE_TYPES];
    if (extra) parts.push(extra);
    if (!isIOS()) parts.push(HEIC_TYPES);
    parts.push(PDF_TYPES);
    return parts.join(',');
  }

  function kindOf(file) {
    if (!file) return '';
    var t = (file.type || '').toLowerCase();
    var n = (file.name || '').toLowerCase();
    if (t === 'application/pdf' || /\.pdf$/.test(n)) return 'pdf';
    // Windows + Chrome report .heic with an empty type.
    if (t === 'image/heic' || t === 'image/heif' || /\.hei[cf]$/.test(n)) return 'heic';
    if (t.indexOf('image/') === 0) return 'image';
    return '';
  }
  function needsConversion(file) { var k = kindOf(file); return k === 'pdf' || k === 'heic'; }
  /* `alsoImages`: true → any image/* passes (the studio decodes webp/gif too). */
  function isSupported(file, alsoImages) {
    var k = kindOf(file);
    if (k === 'pdf' || k === 'heic') return true;
    if (k !== 'image') return false;
    return alsoImages ? true : /^image\/(jpe?g|png)$/i.test(file.type);
  }
  function busyText(file) {
    return kindOf(file) === 'pdf' ? 'Preparing your PDF…' : 'Preparing your HEIC photo…';
  }

  var scripts = {};
  function loadScript(src) {
    if (scripts[src]) return scripts[src];
    scripts[src] = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src; s.async = true;
      s.onload = function () { resolve(); };
      s.onerror = function () { delete scripts[src]; reject(new Error('load ' + src)); };
      document.head.appendChild(s);
    });
    return scripts[src];
  }

  function baseName(file) {
    return String(file.name || 'upload').replace(/\.[^.]+$/, '') || 'upload';
  }
  function canvasToFile(canvas, name, quality) {
    return new Promise(function (resolve, reject) {
      canvas.toBlob(function (blob) {
        if (!blob) { reject(new Error('encode')); return; }
        resolve(new File([blob], name + '.jpg', { type: 'image/jpeg' }));
      }, 'image/jpeg', quality);
    });
  }
  function fitScale(w, h, maxEdge) {
    var s = Math.min(1, Math.sqrt(maxArea() / (w * h)));
    if (maxEdge) s = Math.min(s, maxEdge / Math.max(w, h));
    return s;
  }

  // Safari (macOS 17+ / iOS) decodes HEIC natively; Chrome/Firefox/Edge do not.
  function heicNative(file) {
    return new Promise(function (resolve, reject) {
      var url = URL.createObjectURL(file);
      var img = new Image();
      img.onload = function () {
        try {
          var w = img.naturalWidth, h = img.naturalHeight;
          if (!(w > 0 && h > 0)) throw new Error('empty');
          var s = fitScale(w, h);
          var c = document.createElement('canvas');
          c.width = Math.round(w * s); c.height = Math.round(h * s);
          c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
          URL.revokeObjectURL(url);
          resolve(canvasToFile(c, baseName(file), PHOTO_QUALITY));
        } catch (e) { URL.revokeObjectURL(url); reject(e); }
      };
      img.onerror = function () { URL.revokeObjectURL(url); reject(new Error('no native HEIC')); };
      img.src = url;
    });
  }
  function heicLibrary(file) {
    return loadScript(HEIC2ANY_SRC).then(function () {
      return root_().heic2any({ blob: file, toType: 'image/jpeg', quality: PHOTO_QUALITY });
    }).then(function (out) {
      var blob = Array.isArray(out) ? out[0] : out;   // a burst/live HEIC holds several
      return new File([blob], baseName(file) + '.jpg', { type: 'image/jpeg' });
    });
  }
  function convertHeic(file) {
    return heicNative(file).catch(function () { return heicLibrary(file); })
      .then(function (f) { return { file: f, converted: 'heic' }; })
      .catch(function (e) {
        console.error('PFC HEIC conversion failed', e);
        throw new Error('We couldn’t read that HEIC photo — please save it as a JPG and try again.');
      });
  }

  function convertPdf(file) {
    var lib;
    return loadScript(PDFJS_SRC).then(function () {
      lib = root_().pdfjsLib;
      lib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_SRC;
      return file.arrayBuffer();
    }).then(function (buf) {
      return lib.getDocument({ data: new Uint8Array(buf) }).promise;
    }).then(function (doc) {
      return doc.getPage(1).then(function (page) {
        var vp1 = page.getViewport({ scale: 1 });
        var scale = Math.min(PDF_MAX_EDGE / Math.max(vp1.width, vp1.height),
                             Math.sqrt(maxArea() / (vp1.width * vp1.height)));
        var vp = page.getViewport({ scale: scale });
        var c = document.createElement('canvas');
        c.width = Math.floor(vp.width); c.height = Math.floor(vp.height);
        var ctx = c.getContext('2d');
        ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, c.width, c.height);   // JPEG has no alpha
        return page.render({ canvasContext: ctx, viewport: vp }).promise.then(function () {
          return canvasToFile(c, baseName(file), PDF_QUALITY);
        }).then(function (f) {
          var pages = doc.numPages;
          try { doc.destroy(); } catch (_) {}
          return {
            file: f, converted: 'pdf', pages: pages,
            note: pages > 1 ? 'Your PDF has ' + pages + ' pages — we’ve used page 1.' : '',
          };
        });
      });
    }).catch(function (e) {
      console.error('PFC PDF conversion failed', e);
      var pw = e && e.name === 'PasswordException';
      var err = new Error(pw
        ? 'That PDF is password-protected — please remove the password and try again.'
        : 'We couldn’t read that PDF — please try a JPG or PNG instead.');
      err.final = pw || (e && e.name === 'InvalidPDFException');
      throw err;
    });
  }

  function root_() { return typeof self !== 'undefined' ? self : window; }

  /*
   * Resolve { file, converted?, pages?, note? }. `file` is the input untouched
   * unless it was HEIC/PDF, in which case it is a JPEG File. Rejects with an
   * Error whose message is fit to show the customer.
   */
  function normalise(file) {
    var k = kindOf(file);
    var run = k === 'heic' ? convertHeic : k === 'pdf' ? convertPdf : null;
    if (!run) return Promise.resolve({ file: file });
    // One retry: the first conversion in a fresh Safari tab once failed and never
    // again in later runs (2026-10-04). A broken or locked PDF fails the same way
    // twice, so it is not retried.
    return run(file).catch(function (e) {
      if (e && e.final) throw e;
      return run(file);
    });
  }

  return {
    accept: accept,
    kindOf: kindOf,
    needsConversion: needsConversion,
    isSupported: isSupported,
    busyText: busyText,
    normalise: normalise,
    LABEL: 'JPG, PNG, HEIC or PDF',
  };
});

